import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { clsx, createUseStyles } from '@pushpay/styles';
import { FocusLock, useLockBodyScroll, useOnClickOutside } from '@pushpay/utils';
const transitionDuration = 0.3;

const openTransition = name => `${name} ${transitionDuration}s cubic-bezier(0.7, 0.3, 0.1, 1)`;

const closeTransition = name => `${name} ${transitionDuration}s ease-out`;

const useStyles = createUseStyles(theme => ({
  root: {
    position: 'relative',
    zIndex: theme.ELEVATION.MODAL.Z_INDEX
  },
  drawer: {
    position: 'fixed',
    transition: closeTransition('transform')
  },
  top: {
    top: 0,
    left: 0,
    right: 0,
    transform: 'translateY(-100%)'
  },
  right: {
    right: 0,
    top: 0,
    bottom: 0,
    transform: 'translateX(100%)'
  },
  bottom: {
    left: 0,
    right: 0,
    bottom: 0,
    transform: 'translateY(100%)'
  },
  left: {
    left: 0,
    top: 0,
    bottom: 0,
    transform: 'translateX(-100%)'
  },
  pulledDrawer: {
    transform: 'translate(0)',
    transition: openTransition('transform')
  },
  mask: {
    position: 'fixed',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    opacity: 0,
    backgroundColor: theme.colors['palette-black'],
    transition: closeTransition('opacity')
  },
  visibleMask: {
    opacity: 0.45,
    transition: openTransition('opacity')
  }
}));
export const Drawer = ({
  visible,
  onClose,
  classes: classesProp,
  className: classNameProp,
  children,
  placement,
  closeOnClickOutside,
  'data-pp-at-target': targetId,
  ...rest
}) => {
  const containerRef = useRef();
  const [isPulled, setIsPulled] = useState(false);
  const drawerRef = useRef(null);
  const onPressEscape = useCallback(({
    key
  }) => {
    if (key === 'Escape') {
      onClose();
    }
  }, [onClose]);
  useEffect(() => {
    containerRef.current = document.createElement('div');
    containerRef.current.setAttribute('data-pp-at-target', `${targetId}-container`);
    document.body.appendChild(containerRef.current);
    return () => {
      if (containerRef.current) document.body.removeChild(containerRef.current);
    };
  }, [targetId]);
  useLockBodyScroll(visible);
  useEffect(() => {
    let timeoutId;

    if (visible) {
      setIsPulled(true);
      document.body.addEventListener('keydown', onPressEscape);
    } else if (isPulled) {
      window.setTimeout(() => {
        setIsPulled(false);
      }, transitionDuration * 1000);
    }

    return () => {
      document.body.removeEventListener('keydown', onPressEscape);

      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [isPulled, onPressEscape, visible]);
  useOnClickOutside([drawerRef, ...(Array.isArray(closeOnClickOutside) ? closeOnClickOutside : [])], onClose, !!closeOnClickOutside && visible);
  const classes = useStyles(classesProp);
  if (!visible && !isPulled || !containerRef.current) return null;
  const className = clsx(classes.root, classNameProp);
  return /*#__PURE__*/createPortal( /*#__PURE__*/React.createElement("div", Object.assign({
    className: className
  }, rest), /*#__PURE__*/React.createElement("div", {
    "aria-hidden": true,
    onClick: closeOnClickOutside ? undefined : onClose,
    className: clsx(classes.mask, visible && isPulled && classes.visibleMask),
    "data-pp-at-target": `${targetId}-mask`
  }), /*#__PURE__*/React.createElement(FocusLock, {
    returnFocus: true,
    autoFocus: true
  }, /*#__PURE__*/React.createElement("div", {
    ref: drawerRef,
    role: "dialog",
    "aria-modal": true,
    className: clsx(classes[placement], classes.drawer, visible && isPulled && classes.pulledDrawer),
    "data-pp-at-target": `${targetId}-drawer`
  }, children))), containerRef.current);
};